import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as RevenueIcon } from '../../../assets/icons/sidebar/revenue-sharing-icon.svg';
import { brandText } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { AccountRevenueSharingInterFace } from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchAccountRevenueSharingList,
  fetchListAccountRevenueSharingFilters,
  updateAccountRevenueSharingFilters,
  setSortData,
} from '../../../redux/slices/AccountRevenueSharing';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { disablePaymentManagementApplied } from '../../../redux/slices/PaymentManagementSlice';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import CpoCdrSidePanel from '../../../shared-components/table-side-panel/CpoCdrSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import {
  getDaysBetween,
  removeEmptyKeys,
  changeDateFormat,
} from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import revenueSharingApi from '../../../utils/api/revenue';

/**
 * Revenue sharing component
 * @returns {React.JSX.Element} returning jsx element
 */
function AccountRevenueSharing() {
  const { t, i18n } = useTranslation('revenue');

  const dispatch = useAppDispatch();
  const {
    revenueList,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.revenueSharing);

  const [searchParameters, setSearchParameters] = useSearchParams();
  const [modalView, setModalView] = useState({
    status: false,
    title: t('addAccount'),
    header: true,
    content: <div />,
    rightCorner: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const currentDate = new Date();
  const pastDate = new Date();
  pastDate.setDate(currentDate.getDate() - 13);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: changeDateFormat(pastDate.toString()),
    endDate: changeDateFormat(currentDate.toString()),
  });

  /**
   * Clears the date range values
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters
   * @returns {any} The result
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  /**
   * Fetch Users list function
   * @param {any} data - The request data
   */
  const fetchRevenueSharing = (data: any) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading) {
      dispatch(fetchAccountRevenueSharingList(data));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchRevenueSharing({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page,
      limit,
      sortField,
      sortBy,
    });
  };

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: revenueList,
  });

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchRevenueSharing({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      sortField,
      sortBy,
    });
  };

  const column = [
    {
      label: t('cdrId'),
      accessorKey: 'cdrId' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      queryParamsKey: 'transactionObjectId',
      queryParamsValue: 'chargingTransactionId',
      sidePanelComponent: <CpoCdrSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Rendering converted UTC date and time
       * @param RowData - current row data
       * @returns - returns span element with date and time
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ConvertUTCtoDateTime(RowData.createdAt)}</span>
      ),
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: `${t('totalPowerConsumed')} (kWh)`,
      accessorKey: 'powerConsumedInKwh' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Rendering total power consumption
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.powerConsumedInKwh, 3)} kWh</span>
      ),
    },
    {
      label: `${t('totalAmount')}`,
      accessorKey: 'totalAmount' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      columnWidth: 160,
      /**
       * total power
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>
          {ToFixedValue(RowData?.totalAmount)} {RowData?.currency}
        </span>
      ),
    },
    {
      label: t('energyCost'),
      accessorKey: 'energyCost' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering energy cost
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>
          {ToFixedValue(RowData?.energyCost)} {RowData?.currency}
        </span>
      ),
    },
    {
      label: t('sharableRevenue'),
      accessorKey: 'sharableRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Rendering sharable revenue
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>
          {ToFixedValue(RowData?.sharableRevenue)} {RowData?.currency}
        </span>
      ),
    },
    {
      label: t('partnerRevenueByPercentage'),
      accessorKey: 'partnerRevenuePercentage' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering partner revenue by percentage
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.partnerRevenuePercentage)} %</span>
      ),
    },
    {
      label: `${t('partnerRevenueByCost')}`,
      accessorKey: 'partnerRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering partner revenue cost
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>
          {ToFixedValue(RowData?.partnerRevenue)} {RowData?.currency}
        </span>
      ),
    },
    {
      label: t('cpoRevenueByPercentage'),
      accessorKey: 'cpoRevenuePercentage' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering CPO revenue by percentage
       * @param {AccountRevenueSharingInterFace} RowData - The row data
       * @returns {React.JSX.Element} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.cpoRevenuePercentage)} %</span>
      ),
    },
    {
      label: `${t('cpoRevenueByCost')}`,
      accessorKey: 'cpoRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      columnWidth: 190,
      /**
       * Rendering CPO revenue by cost
       * @param {AccountRevenueSharingInterFace} RowData - The row data
       * @returns {React.JSX.Element} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>
          {ToFixedValue(RowData?.cpoRevenue)} {RowData?.currency}
        </span>
      ),
    },
  ];

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('cdrId'), accessorKey: 'cdrId' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('accountName'), accessorKey: 'accountName' },
    { label: t('accountId'), accessorKey: 'accountCustomId' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: `${t('totalPowerConsumed')} (Kwh)`, accessorKey: 'powerConsumedInKwh' },
    { label: `${t('totalAmount')} (${brandText.currency})`, accessorKey: 'totalAmount' },
    { label: t('energyCost'), accessorKey: 'energyCost' },
    { label: t('sharableRevenue'), accessorKey: 'sharableRevenue' },
    { label: t('partnerRevenueByPercentage'), accessorKey: 'partnerRevenuePercentage' },
    { label: t('partnerRevenueByCost'), accessorKey: 'partnerRevenue' },
    { label: t('cpoRevenuePercentage'), accessorKey: 'cpoRevenuePercentage' },
    { label: t('cpoRevenueByCost'), accessorKey: 'cpoRevenue' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = removeEmptyKeys({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy,
      sortField,
    });
    revenueSharingApi
      .fetchRevenueSharingExport(removeEmptyKeys(payload))
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AccountRevenueSharingInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'cdrId':
                return row.cdrId || t('na');

              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDateTime(row.createdAt) : t('na');

              case 'accountCustomId':
                return row.accountCustomId || t('na');

              case 'powerConsumedInKwh':
                return row.powerConsumedInKwh
                  ? ToFixedValue(Number(row.powerConsumedInKwh), 3)
                  : '0.000';

              case 'totalAmount':
                return row.totalAmount ? ToFixedValue(row.totalAmount, 2) : '0.00';

              case 'energyCost':
                return row.energyCost ? ToFixedValue(row.energyCost, 2) : '0.00';

              case 'sharableRevenue':
                return row.sharableRevenue ? ToFixedValue(row.sharableRevenue, 2) : '0.00';

              case 'partnerRevenuePercentage':
                return row.partnerRevenuePercentage
                  ? ToFixedValue(row.partnerRevenuePercentage, 2)
                  : '0.00';

              case 'partnerRevenue':
                return row.partnerRevenue ? ToFixedValue(row.partnerRevenue, 2) : '0.00';

              case 'cpoRevenuePercentage':
                return row.cpoRevenuePercentage
                  ? ToFixedValue(row.cpoRevenuePercentage, 2)
                  : '0.00';

              case 'cpoRevenue':
                return row.cpoRevenue ? ToFixedValue(row.cpoRevenue, 2) : '0.00';

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-sharing-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-sharing-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 404,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchRevenueSharing({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (isFilterUpdated || revenueList.length === 0 || typeof dateRange.startDate === 'string') {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchRevenueSharing({
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
      });
      dispatch(disablePaymentManagementApplied());
    }
  }, [filters, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('revenueSharing'), icon: <RevenueIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  useEffect(() => {
    if (
      searchParameters.get('deviceId') ||
      searchParameters.get('userCustomId') ||
      searchParameters.get('transactionObjectId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceId');
      parameters.delete('userCustomId');
      parameters.delete('transactionObjectId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <CustomTable
        dataKey="AccountRevenueSharing"
        header={column}
        version="v2"
        height={window.innerHeight >= 700 ? 337 : 320}
        data={revenueList}
        serialNo
        sizeChangeButtonRequired
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        filters={filters}
        filtersToDispatch={updateAccountRevenueSharingFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListAccountRevenueSharingFilters}
        refershCurrentPages={handleRefresh}
        exportDataPdfOrCSV={
          permissionCheck('cpoRevenueSharingExport') ? exportDataPdfOrCSV : undefined
        }
        exportLoader={exportLoader}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
        rightTopContent={renderDateFilters()}
        onClearFilterChange={() => {
          clearDateRange();
        }}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={handleSidePanelClose}
      />
    </div>
  );
}

export default AccountRevenueSharing;
